import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Search, Users, ShoppingBag, TrendingUp, Mail, Phone, Calendar,
  ChevronUp, ChevronDown, X, Package, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtCurrency = (n) => `₹${fmt(n)}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_COLORS = {
  placed:           "bg-blue-100 text-blue-700",
  accepted:         "bg-indigo-100 text-indigo-700",
  packed:           "bg-purple-100 text-purple-700",
  shipped:          "bg-amber-100 text-amber-700",
  out_for_delivery: "bg-orange-100 text-orange-700",
  delivered:        "bg-green-100 text-green-700",
  cancelled:        "bg-red-100 text-red-700",
  paid:             "bg-green-100 text-green-700",
  pending:          "bg-yellow-100 text-yellow-700",
  failed:           "bg-red-100 text-red-700",
};

function Avatar({ name }) {
  const colors = ["bg-purple-500","bg-blue-500","bg-emerald-500","bg-amber-500","bg-rose-500","bg-cyan-500","bg-indigo-500","bg-teal-500"];
  const bg = colors[(name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
      {(name || "?")[0].toUpperCase()}
    </div>
  );
}

function SortIcon({ field, sort }) {
  if (sort.field !== field) return <ChevronUp className="w-3 h-3 text-slate-300" />;
  return sort.dir === "asc"
    ? <ChevronUp className="w-3 h-3 text-orange-500" />
    : <ChevronDown className="w-3 h-3 text-orange-500" />;
}

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [sort, setSort]         = useState({ field: "created_at", dir: "desc" });
  const [selected, setSelected] = useState(null); // customer detail modal
  const [custOrders, setCustOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const openCustomer = async (c) => {
    setSelected(c);
    setCustOrders([]);
    setOrdersLoading(true);
    try {
      const token = localStorage.getItem("it_token");
      const res = await axios.get(`${API}/admin/customer-orders`, {
        params: { emails: (c.emails || [c.email]).join(","), phones: (c.phones || [c.phone]).join(",") },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustOrders(res.data);
    } catch {
      setCustOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchCustomers = async (q = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("it_token");
      const res = await axios.get(`${API}/admin/customers`, {
        params: { search: q },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomers(res.data);
    } catch {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchCustomers(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const sorted = useMemo(() => {
    return [...customers].sort((a, b) => {
      let av = a[sort.field], bv = b[sort.field];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [customers, sort]);

  const toggleSort = (field) => {
    setSort(prev => prev.field === field
      ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { field, dir: "desc" }
    );
  };

  // KPI summaries
  const totalSpent   = customers.reduce((s, c) => s + c.total_spent, 0);
  const totalOrders  = customers.reduce((s, c) => s + c.total_orders, 0);
  const withOrders   = customers.filter(c => c.total_orders > 0).length;

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-black text-slate-900">Customers</h1>
        <p className="text-sm text-slate-500 mt-0.5">Everyone who has logged in or placed an order, matched by email or phone number</p>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Customers", value: fmt(customers.length), icon: Users,       color: "bg-indigo-50 text-indigo-600" },
          { label: "With Orders",     value: fmt(withOrders),        icon: ShoppingBag, color: "bg-amber-50 text-amber-600" },
          { label: "Total Orders",    value: fmt(totalOrders),        icon: ShoppingBag, color: "bg-teal-50 text-teal-600" },
          { label: "Total Revenue",   value: fmtCurrency(totalSpent), icon: TrendingUp,  color: "bg-emerald-50 text-emerald-600" },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.color}`}>
              <k.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-400">{k.label}</p>
              <p className="text-xl font-black text-slate-900">{k.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 gap-3 flex-wrap">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email or phone…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-orange-400"
            />
          </div>
          <span className="text-xs text-slate-400 flex-shrink-0">{fmt(customers.length)} customers</span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">No customers found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-400">Customer</th>
                  {[
                    { label: "Email",        field: "email" },
                    { label: "Phone",        field: "phone" },
                    { label: "Orders",       field: "total_orders" },
                    { label: "Total Spent",  field: "total_spent" },
                    { label: "Joined",       field: "created_at" },
                    { label: "Last Order",   field: "last_order" },
                  ].map(col => (
                    <th key={col.field}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-400 cursor-pointer hover:text-slate-600 select-none whitespace-nowrap"
                      onClick={() => toggleSort(col.field)}
                    >
                      <span className="flex items-center gap-1">
                        {col.label} <SortIcon field={col.field} sort={sort} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sorted.map(c => (
                  <tr key={c.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => openCustomer(c)}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={c.name} />
                        <span className="font-semibold text-slate-800">{c.name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{c.phone || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${c.total_orders > 0 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-400"}`}>
                        {c.total_orders}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{fmtCurrency(c.total_spent)}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.last_order)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Customer Detail Drawer ──────────────────────────────────────── */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end"
          onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
              <h2 className="font-black text-slate-900">Customer Details</h2>
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {/* Profile */}
            <div className="px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-4 mb-4">
                <Avatar name={selected.name} />
                <div>
                  <p className="font-black text-slate-900 text-base">{selected.name || "No name"}</p>
                  <p className="text-xs text-slate-400">{selected.registered ? "Registered" : "Guest (ordered without logging in)"} · Since {fmtDate(selected.created_at)}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail className="w-4 h-4 text-slate-400" />{selected.email || "—"}
                </div>
                {selected.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />{selected.phone}
                  </div>
                )}
                {selected.address && (
                  <p className="flex items-start gap-2 text-sm text-slate-600">
                    <Package className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />{selected.address}
                  </p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 px-6 py-5 border-b border-slate-100">
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-black text-orange-600">{selected.total_orders}</p>
                <p className="text-xs text-slate-500 mt-0.5">Total Orders</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-black text-emerald-600">{fmtCurrency(selected.total_spent)}</p>
                <p className="text-xs text-slate-500 mt-0.5">Total Spent</p>
              </div>
            </div>

            {/* Order History */}
            <div className="px-6 py-5 flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-4 h-4 text-slate-400" />
                <h3 className="font-bold text-slate-800 text-sm">Order History</h3>
                {custOrders.length > 0 && (
                  <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-semibold">
                    {custOrders.length} orders
                  </span>
                )}
              </div>

              {ordersLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-6 h-6 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : custOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                  <ShoppingBag className="w-10 h-10 mb-2 opacity-30" />
                  <p className="text-sm">No orders yet</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {custOrders.map((o, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-3.5 border border-slate-100 hover:border-orange-200 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-xs font-black text-slate-800 font-mono">#{o.order_number}</p>
                          <div className="flex items-center gap-1 mt-0.5 text-slate-400">
                            <Clock className="w-3 h-3" />
                            <span className="text-[11px]">{fmtDate(o.created_at)}</span>
                          </div>
                        </div>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[o.status] || "bg-slate-100 text-slate-600"}`}>
                          {o.status?.replace(/_/g, " ")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] text-slate-500">
                            {o.items_count} item{o.items_count !== 1 ? "s" : ""}
                          </span>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[o.payment_status] || "bg-slate-100 text-slate-600"}`}>
                            {o.payment_status}
                          </span>
                        </div>
                        <p className="text-sm font-black text-slate-900">{fmtCurrency(o.amount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
